import {
  AlternativeIcon,
  DeleteIcon,
  MarkNotAvailableIcon,
  SubstituteIcon,
  TotalDocumentsIcon,
  UploadFileIcon,
  VerticalDotsIcon,
} from 'components/common/icons';
import { useNavigate } from 'react-router-dom';
import { ADMIN_ROUTES } from 'constants/routes';
import { PlusOutlined } from '@ant-design/icons';
import { PERMISSIONS } from 'constants/permissions';
import { AddItemModal } from 'pages/admin/items/addItem';
import { useUpdateSupplierOfferItemMutation } from 'api';
import { AddEditModal } from 'components/common/attachments/addEdit';
import { useNotificationMessages } from 'hooks/useNotificationMessages';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAccessControl } from 'components/common/accessControl/useAccessControl';
import { DeleteModal } from 'feature/purchaseRequestTabs/supplierOffers/edit/delete';
import { capitalizeWords, isNil, preventNonNumericValue, toNumberOrMinusOne } from 'utils/helpers';
import { Button, Col, Dropdown, HasAccess, InputNumber, MenuProps, Row, Table, Tag, Typography } from 'components';
import { DisplayColumnsDropdown } from 'feature/purchaseRequestTabs/supplierOffers/edit/SupplierOfferItems/displayColumnDropdown';
import { SupplierOfferItem, SupplierOfferItemsTableProps } from './types';
import { MarkNotAvailableModel } from './../markNotAvailable';
import { AddAlternativeModel } from './../addAlternative';
import { AttachmentDrawer } from './../attachmentDrawer';
import { AddSubstituteModal } from './../addSubstitute';
import { EditableCell } from './EditableCell';
import { EditableRow } from './EditableRow';
import styles from './styles.module.scss';
import './antd.scss';

const { Text, Title, Paragraph } = Typography;

const calculateTotal = (dataSource: SupplierOfferItem[], key: keyof SupplierOfferItem) => {
  const result = dataSource.reduce((sum, item) => sum + (Number(item[key]) || 0), 0);
  return result === 0 ? 0 : parseFloat(result.toFixed(2)).toString();
};
type ItemType = 'SUBSTITUTE' | 'ALTERNATIVE' | 'NOT_AVAILABLE';

const ITEM_TYPE_MAPPING: Record<ItemType, JSX.Element> = {
  SUBSTITUTE: <SubstituteIcon />,
  ALTERNATIVE: <AlternativeIcon />,
  NOT_AVAILABLE: <MarkNotAvailableIcon color={'#FB3748'} />,
};

const columnsList = [
  { label: '', value: 'action' },
  { label: 'Item Number, SKU', value: 'sku' },
  { label: 'Item Description', value: 'description' },
  { label: 'Qty', value: 'quantity' },
  { label: 'Price', value: 'purchasePricePerPiece' },
  { label: 'Discount', value: 'discount' },
  { label: 'Lead Time', value: 'leadTime' },
  { label: 'Shipping Cost', value: 'shippingCost' },
  { label: 'Copper Fee', value: 'copper' },
  { label: 'Fees', value: 'fees' },
  { label: 'Item Dimensions', value: 'dimensions' },
  { label: 'Weight', value: 'weight' },
  { label: 'Notes', value: 'notes' },
  { label: 'Attachments', value: 'attachments' },
  { label: 'Total', value: 'totalPurchasePrice' },
  { label: '', value: 'remove' },
];

export const SupplierOfferItemsTable: React.FC<SupplierOfferItemsTableProps> = ({
  originalForm,
  supplierOfferId,
  initialItems,
  subTable,
  subTableType,
  subTableBrand,
  subTableBrandId,
  subTableItemNumber,
  subTableSku,
  originalItemId,
  globalShippingCost,
  visibleColumns,
  setVisibleColumns,
  purchaseRequest,
}) => {
  const [count, setCount] = useState(0);
  const [editingRow, setEditingRow] = useState<string>('');
  const [editingColumn, setEditingColumn] = useState<string>('');
  const [deleteRecord, setDeleteRecord] = useState<boolean>(false);
  const [addItemModalState, setAddItemModalState] = useState<boolean>(false);
  const [dataSource, setDataSource] = useState<SupplierOfferItem[]>([]);
  const [record, setRecord] = useState<Record<string, any> | undefined>();
  const [attachmentDrawerState, setAttachmentDrawerState] = useState<boolean>(false);
  const [addSubstituteModalState, setAddSubstituteModalState] = useState<boolean>(false);
  const [addAttachmentModalState, setAttachmentItemModalState] = useState<boolean>(false);
  const [addAlternativeModalState, setAddAlternativeModalState] = useState<boolean>(false);
  const [markNotAvailableModalState, setMarkNotAvailableModalState] = useState<boolean>(false);
  const [drawerAttachments, setDrawerAttachments] = useState<Record<string, any>[] | undefined>();
  const { handleError, handleWarning, handleSuccess } = useNotificationMessages();
  const { hasAccess } = useAccessControl();
  const navigate = useNavigate();
  const [updateSupplierOfferItem] = useUpdateSupplierOfferItemMutation();
  const isViewOnly = !hasAccess(PERMISSIONS.purchaseRequests.supplierOffers.write) || purchaseRequest?.isFinalStatus;

  // Convert initialItems to the expected format and set as dataSource
  useEffect(() => {
    if (initialItems && initialItems.length > 0) {
      const formattedItems = initialItems.map((item: any, index: number) => ({
        ...item,
        key: item.id || `${index}`,
      }));
      setDataSource(formattedItems);
      setCount(initialItems.length);
    } else {
      setDataSource([]);
      setCount(0);
    }
  }, [initialItems]);
  const handleAdd = () => {
    const newItemKey = `new-${count}`;
    const newItem: SupplierOfferItem = {
      key: newItemKey,
      sku: '',
      description: '',
      uuid: undefined,
      quantity: undefined,
      purchasePricePerPiece: undefined,
      attachment: undefined,
      discount: undefined,
      shippingCost: undefined,
      dimensions: undefined,
      fees: undefined,
      weight: undefined,
      copper: undefined,
      leadTimeMin: undefined,
      leadTimeMax: undefined,
      leadTime: undefined,
      notes: undefined,
      totalPurchasePrice: undefined,
    };
    setDataSource([newItem, ...dataSource]);
    setCount(count + 1);

    setEditingRow(newItemKey);
    setEditingColumn('itemNumber');
  };

  const handleGlobalDiscountBlur = useCallback(
    async (value: number) => {
      const payload = initialItems.map((item: any) => {
        const result: Record<string, any> = {
          id: item.id,
          item: {
            discount: value,
          },
        };
        if (item.itemActionType) {
          const subItemType = item.itemActionType === 'ALTERNATIVE' ? 'alternativeItems' : 'substituteItems';
          result[subItemType] = [];
          Object.values(item.alternativeSubstituteItem || {})
            .flat()
            .forEach((_item: any) => {
              result[subItemType].push({
                brandId: _item.brandId,
                id: _item?.id,
                itemNumber: _item?.itemNumber,
                item: {
                  discount: value,
                },
              });
            });
        }
        return result;
      });
      try {
        const { data, error } = await updateSupplierOfferItem({
          data: payload,
          supplierOfferId,
        });
        if (data?.success) {
          handleSuccess('Discount was successfully updated');
        } else {
          handleError(error);
        }
      } catch (err) {
        handleError(err);
      }
    },
    [initialItems, handleSuccess, handleError, supplierOfferId, updateSupplierOfferItem]
  );

  const handleSave = async (row: SupplierOfferItem) => {
    try {
      await originalForm.validateFields();
      if (!row.sku || row.sku.trim() === '') {
        handleWarning('Sku is required!');
        return;
      }
      const payload = {
        id: row.id,
        sku: row.sku,
        notes: row.notes,
        brandId: row.brandId,
        uuid: row.uuid || null,
        itemNumber: row.itemNumber,
        description: row.description,
        leadTime: row.leadTime || undefined,
        dimensions: row.dimensions || undefined,
        weight: isNil(row.weight) ? undefined : toNumberOrMinusOne(row.weight as string),
        copper: isNil(row.copper) ? undefined : toNumberOrMinusOne(row.copper as string),
        quantity: isNil(row.quantity) ? undefined : toNumberOrMinusOne(row.quantity as string),
        discount: isNil(row.discount) ? undefined : toNumberOrMinusOne(row.discount as string),
        leadTimeMin: isNil(row.leadTimeMin) ? undefined : toNumberOrMinusOne(row.leadTimeMin as string),
        leadTimeMax: isNil(row.leadTimeMax) ? undefined : toNumberOrMinusOne(row.leadTimeMax as string),
        shippingCost: isNil(row.shippingCost) ? undefined : toNumberOrMinusOne(row.shippingCost as string),
        purchasePricePerPiece: isNil(row.purchasePricePerPiece)
          ? undefined
          : toNumberOrMinusOne(row.purchasePricePerPiece as string),
      };
      const { data, error } = await updateSupplierOfferItem(
        subTable
          ? {
              data: [
                {
                  id: originalItemId,
                  item: {
                    itemNumber: originalItemId,
                    itemActionType: subTableType,
                  },
                  [subTableType === 'ALTERNATIVE' ? 'alternativeItems' : 'substituteItems']: [
                    {
                      brandId: subTableBrandId,
                      id: row?.id,
                      itemNumber: row?.itemNumber,
                      item: { ...payload },
                    },
                  ],
                },
              ],
              supplierOfferId,
            }
          : {
              data: [
                {
                  id: row.id,
                  item: { ...payload },
                },
              ],
              supplierOfferId,
            }
      );
      if (data?.success) {
        //handleSuccess(row.id ? 'Supplier offer was successfully updated' : 'Supplier offer was successfully added');

        // Update the dataSource with the updated data
        const newData = [...dataSource];
        const index = newData.findIndex((item) => row.key === item.key);

        // Replace the item with the updated item, including the new ID from the API response
        if (index > -1) {
          const item = newData[index];
          const updatedItem = {
            ...item,
            ...row,
            id: item.id,
            // Use the returned ID or keep the existing one
          };
          newData.splice(index, 1, updatedItem);
          setDataSource(newData);
        }

        // Reset editing state
        setEditingRow('');
        setEditingColumn('');
      } else {
        handleError(error);
      }
    } catch (err: any) {
      setEditingRow('');
      setEditingColumn('');
      const error: any = {
        data: {
          success: false,
          data: {},
        },
      };
      err?.errorFields?.forEach((_error: any, index: string) => {
        error.data.data[index] = _error.errors[0];
      });
      handleError(error);
    }
  };

  const components = {
    body: {
      row: (props: any) => <EditableRow {...props} editingRow={editingRow} editingColumn={editingColumn} />,
      cell: EditableCell,
    },
  };

  const onAddItemModalStateChange = useCallback(() => setAddItemModalState((oState) => !oState), []);
  const onAttachmentExpand = useCallback((value?: Record<string, any>[], record?: any) => {
    setRecord(record);
    setDrawerAttachments(value);
    setAttachmentDrawerState((oState) => !oState);
  }, []);
  const getMenuItems = (record: any): MenuProps['items'] => [
    {
      key: 'substitute',
      label: 'Add Substitute',
      icon: <SubstituteIcon />,
      disabled: record.itemActionType === 'ALTERNATIVE' || record.itemActionType === 'NOT_AVAILABLE',
    },
    {
      key: 'alternative',
      label: 'Add Alternative',
      icon: <AlternativeIcon />,
      disabled: record.itemActionType === 'SUBSTITUTE' || record.itemActionType === 'NOT_AVAILABLE',
    },
    {
      key: 'notAvailable',
      label: record.itemActionType === 'NOT_AVAILABLE' ? 'Mark as Available' : 'Mark Not Available',
      icon: <MarkNotAvailableIcon />,
    },
    {
      key: 'upload',
      label: 'Upload File',
      icon: <UploadFileIcon />,
      disabled: record.itemActionType === 'SUBSTITUTE' || record.itemActionType === 'NOT_AVAILABLE',
    },
  ];

  const handleMenuClick = useCallback((info: any, record: any) => {
    const { key } = info;
    switch (key) {
      case 'substitute':
        return handleAddSubstitute(record);
      case 'alternative':
        return handleAddAlternative(record);
      case 'notAvailable':
        return handleMarkNotAvailable(record);
      case 'upload':
        return onAttachmentAddModalStateChange(record);
    }
  }, []);

  const onRedirectToBrand = useCallback(
    (value: string) => {
      navigate(`${ADMIN_ROUTES.brand.view}/${value}/management`);
    },
    [navigate]
  );
  const onRedirectToItem = useCallback(
    (value: string) => {
      navigate(`${ADMIN_ROUTES.items.view}/${value}/suppliers`);
    },
    [navigate]
  );

  const handleAddSubstitute = (record: any) => {
    setRecord(record);
    setAddSubstituteModalState(true);
  };
  const handleAddAlternative = (record: any) => {
    setRecord(record);
    setAddAlternativeModalState(true);
  };
  const handleMarkNotAvailable = (record: any) => {
    setRecord(record);
    setMarkNotAvailableModalState(true);
  };
  const onAttachmentAddModalStateChange = (record: any) => {
    setRecord(record);
    setAttachmentItemModalState((oState) => !oState);
  };

  const defaultColumns: any[] = useMemo(
    () => [
      {
        title: 'Item Number, SKU',
        dataIndex: 'sku',
        editable: !isViewOnly,
        width: '148px',
        render: (text: string, record: any) => {
          return (
            <Row>
              <Col span={24}>
                <Row align={'middle'} gutter={7} wrap={false}>
                  <Col flex={'auto'}>
                    <Text
                      ellipsis={{
                        tooltip: true,
                      }}
                    >
                      {record.uuid || '-'}
                    </Text>
                  </Col>
                  {!subTable && <Col>{ITEM_TYPE_MAPPING[record.itemActionType as ItemType]}</Col>}
                </Row>
              </Col>
              <Col span={24}>
                <Text
                  ellipsis={{
                    tooltip: text,
                  }}
                >
                  {text || '-'}
                </Text>
              </Col>
            </Row>
          );
        },
      },
      {
        title: 'Item Description',
        dataIndex: 'description',
        editable: !isViewOnly,
        width: '290px',
        render: (text: string) => (
          <Paragraph className={'reset-title'} ellipsis={{ rows: 2, tooltip: true }}>
            {text || '-'}
          </Paragraph>
        ),
      },
      {
        title: 'Qty',
        dataIndex: 'quantity',
        editable: !subTable && !isViewOnly,
        width: '60px',
        render: (value: string) => (
          <Text
            className={'no-wrap'}
            ellipsis={{
              tooltip: true,
            }}
            style={{
              maxWidth: '50px',
            }}
          >
            {Number(value) || '-'}
          </Text>
        ),
      },
      {
        title: 'Price',
        dataIndex: 'purchasePricePerPiece',
        editable: !isViewOnly,
        inputType: 'number',
        width: '100px',
        render: (value: number) => <Text className={'no-wrap'}>{Number(value) ? `${value}€` : '-'}</Text>,
      },
      {
        title: 'Discount',
        dataIndex: 'discount',
        editable: !isViewOnly,
        inputType: 'number',
        width: '100px',
        render: (value: number) => <Text className={'no-wrap'}> {Number(value) ? `${value}%` : '-'}</Text>,
      },
      {
        title: 'Lead Time',
        dataIndex: 'leadTime',
        editable: !isViewOnly,
        width: '160px',
        render: (value: string, record: any) => {
          const { leadTimeMax, leadTimeMin } = record;
          return (
            <Row align={'middle'} gutter={4} wrap={false}>
              <Col flex={'auto'}>
                <Text type={'secondary'} className={'no-wrap'} ellipsis={{ tooltip: true }}>
                  {leadTimeMin && leadTimeMax
                    ? `${leadTimeMin}-${leadTimeMax}`
                    : leadTimeMin
                      ? `${leadTimeMin} Min`
                      : leadTimeMax
                        ? `${leadTimeMax} Max`
                        : '-'}
                </Text>
              </Col>
              {(leadTimeMin || leadTimeMax) && (
                <Col>
                  <Row className={'gap-4'} justify={'center'} wrap={false}>
                    <Tag className={`tag ${value === 'DAY' && 'selected-tag'}`}>Day</Tag>
                    <Tag className={`tag ${value === 'WEEK' && 'selected-tag'} `}>Week</Tag>
                  </Row>
                </Col>
              )}
            </Row>
          );
        },
      },
      {
        title: 'Shipping Cost',
        dataIndex: 'shippingCost',
        editable: !isViewOnly && !globalShippingCost,
        inputType: 'number',
        width: '132px',
        render: (value: number) => <Text className={'no-wrap'}>{Number(value) ? `${value}€` : '-'}</Text>,
      },
      {
        title: 'Copper Fee',
        dataIndex: 'copper',
        editable: !isViewOnly,
        inputType: 'number',
        width: '90px',
        render: (value: number) => {
          return <Text className={'no-wrap'}>{Number(value) ? `${value}%` : '-'}</Text>;
        },
      },
      {
        title: 'Fees',
        dataIndex: 'fees',
        editable: false,
        width: '80px',
        render: (value: number) => <Text className={'no-wrap'}>{Number(value) ? `${value}€` : '-'}</Text>,
      },
      {
        title: 'Item Dimensions L/W/H (cm)',
        dataIndex: 'dimensions',
        editable: !isViewOnly,
        width: '223px',
        render: (value: { length?: number; width?: number; height?: number }) => {
          const { length, width, height } = value || {};
          return (
            <Text className="no-wrap">{`${Number(length) ? (length === -1 ? '-' : length) : '-'} / ${Number(width) ? (width === -1 ? '-' : width) : '-'} / ${Number(height) ? (height === -1 ? '-' : height) : '-'}`}</Text>
          );
        },
      },
      {
        title: 'Weight',
        dataIndex: 'weight',
        editable: !isViewOnly,
        inputType: 'number',
        width: '80px',
        render: (value: number) => {
          return <Text className={'no-wrap'}>{Number(value) ? `${value}kg` : '-'}</Text>;
        },
      },
      {
        title: 'Attch.',
        dataIndex: 'attachments',
        editable: false,
        width: '70px',
        render: (value: Record<string, any>[], record: any) => {
          return value?.length ? (
            <Row wrap={false} align={'middle'}>
              <Col style={{ height: '18px' }}>
                <TotalDocumentsIcon />
              </Col>
              <Col>
                <Button
                  size={'small'}
                  type={'text'}
                  variant={'text'}
                  style={{ maxHeight: '18px' }}
                  className={'underline-button'}
                  onClick={() => onAttachmentExpand(value, record)}
                >
                  <Text underline type={'secondary'}>
                    {value?.length}
                  </Text>
                </Button>
              </Col>
            </Row>
          ) : (
            <Text>-</Text>
          );
        },
      },
      {
        title: 'Notes',
        dataIndex: 'notes',
        editable: !isViewOnly,
        width: '100px',
        render: (value: number) => <Text>{value ? `${value}` : '-'}</Text>,
      },
      {
        title: 'Total',
        dataIndex: 'totalPurchasePrice',
        fixed: 'right',
        width: '150px',
        render: (value: number) => <Text>{value ? `${value.toFixed(2)}€` : '-'}</Text>,
      },
    ],
    [isViewOnly, onAttachmentExpand, subTable, globalShippingCost]
  );

  if (
    hasAccess(PERMISSIONS.purchaseRequests.supplierOffers.delete) &&
    !defaultColumns.find((i) => i.dataIndex === 'remove')
  ) {
    defaultColumns.push({
      fixed: 'right',
      dataIndex: 'remove',
      width: 34,
      key: 'remove',
      render: (value: string, record: any) => {
        return (
          <Row justify={'center'}>
            <HasAccess role={PERMISSIONS.purchaseRequests.supplierOffers.delete}>
              <Button
                disabled={purchaseRequest?.isFinalStatus}
                onClick={() => {
                  setRecord(record);
                  setDeleteRecord(true);
                }}
                style={{ padding: 0, width: 16, height: 16 }}
                type={'text'}
                size={'small'}
                icon={<DeleteIcon />}
              />
            </HasAccess>
          </Row>
        );
      },
    });
  }

  if (
    hasAccess(PERMISSIONS.purchaseRequests.supplierOffers.write) &&
    !defaultColumns.find((i) => i.dataIndex === 'action')
  ) {
    defaultColumns.unshift({
      dataIndex: 'action',
      title: '',
      fixed: 'left',
      width: '40px',
      render: (_: any, record: any) => (
        <Dropdown
          menu={{
            items: subTable ? getMenuItems(record)?.filter((_item) => _item?.key === 'upload') : getMenuItems(record),
            onClick: ({ key }) => handleMenuClick && handleMenuClick({ key }, record),
            className: 'custom-dropdown-menu',
          }}
          placement="bottomLeft"
          trigger={['click']}
        >
          <Button disabled={purchaseRequest?.isFinalStatus} icon={<VerticalDotsIcon />} type={'text'} size={'small'} />
        </Dropdown>
      ),
    });
  }

  const columns = defaultColumns
    .filter((col) => {
      const key = col.dataIndex;
      return (
        visibleColumns.includes(key) ||
        col.dataIndex === 'totalPurchasePrice' ||
        col.dataIndex === 'action' ||
        col.dataIndex === 'remove'
      );
    })
    .map((col) => {
      if (!col.editable) {
        return {
          ...col,
          onCell: (record: SupplierOfferItem) => ({
            className:
              col.dataIndex !== 'action' &&
              col.dataIndex !== 'remove' &&
              (record.itemActionType === 'NOT_AVAILABLE' || record.itemActionType === 'SUBSTITUTE') &&
              'disabled-cell',
          }),
        };
      }

      return {
        ...col,
        onCell: (record: SupplierOfferItem) => ({
          className:
            col.dataIndex !== 'action' &&
            col.dataIndex !== 'remove' &&
            (record.itemActionType === 'NOT_AVAILABLE' || record.itemActionType === 'SUBSTITUTE') &&
            'disabled-cell',
          record,
          editable: col.editable,
          dataIndex: col.dataIndex,
          inputType: col.inputType,
          onAddItemModalStateChange,
          handleSave,
          purchaseRequest,
        }),
      };
    });
  useEffect(() => {
    const allColumnKeys = defaultColumns
      .filter((col) => ['sku', 'description', 'quantity', 'purchasePricePerPiece', 'discount'].includes(col.dataIndex))
      .map((col) => col.dataIndex); // unique keys for all columns
    setVisibleColumns((prevState) => {
      if (
        prevState?.length &&
        prevState.join() !== ['sku', 'description', 'quantity', 'purchasePricePerPiece', 'discount'].join()
      ) {
        return prevState;
      }
      return allColumnKeys;
    });
  }, [defaultColumns, setVisibleColumns]);
  return (
    <>
      <div>
        <Row justify="space-between" align="middle" className={styles.itemsHeaderRow}>
          <Col>
            {subTable ? (
              <Row align={'middle'} className={'gap-4'}>
                <Title level={5} style={{ color: '#05796B' }} className={'reset-title'}>
                  {capitalizeWords(subTableType || '')} for
                </Title>
                <Title
                  level={5}
                  style={{
                    color: '#05796B',
                    fontWeight: 400,
                    cursor: 'pointer',
                  }}
                  className={'reset-title'}
                  onClick={() => onRedirectToItem(subTableItemNumber as string)}
                >
                  &quot;{subTableSku}&quot;
                </Title>
                <Title level={5} style={{ color: '#05796B' }} className={'reset-title'}>
                  item from
                </Title>
                <Title
                  level={5}
                  className={'reset-title'}
                  style={{ color: '#05796B', cursor: 'pointer' }}
                  underline
                  onClick={() => onRedirectToBrand(subTableBrandId as string)}
                >
                  {subTableBrand}
                </Title>
              </Row>
            ) : (
              <Text style={{ fontSize: 18 }} strong>
                Items List
              </Text>
            )}
          </Col>
          {!subTable && (
            <Col>
              <Row className={'gap-12'} align={'bottom'}>
                <Col flex={'131px'}>
                  <Row gutter={[0, 2]}>
                    <Col span={24}>
                      <Text className={'font-size-12 font-weight-600'}>Discount</Text>
                    </Col>
                    <InputNumber<number>
                      disabled={isViewOnly}
                      controls={false}
                      size={'middle'}
                      onBlur={(e) => handleGlobalDiscountBlur(toNumberOrMinusOne(e.target.value))}
                      onPressEnter={(e) => {
                        const input = e.target as HTMLInputElement;
                        handleGlobalDiscountBlur(toNumberOrMinusOne(input.value));
                      }}
                      onBeforeInput={(event: any) => preventNonNumericValue(event)}
                      style={{ width: '100%' }}
                      suffix={<Text type={'secondary'}>%</Text>}
                    />
                  </Row>
                </Col>
                <DisplayColumnsDropdown onVisibleColumnsChange={setVisibleColumns} visibleColumns={visibleColumns} />
              </Row>
            </Col>
          )}
        </Row>
        <div>
          <Table
            components={isViewOnly ? {} : components}
            bordered
            dataSource={dataSource}
            columns={columns as any}
            pagination={false}
            size="middle"
            className={styles.table}
            scroll={{ x: 850 }}
            summary={() =>
              subTable ? null : (
                <Table.Summary fixed>
                  <Table.Summary.Row style={{ backgroundColor: '#F5F7FA' }}>
                    {columnsList
                      .filter((col) => {
                        const key = col.value;
                        return (
                          visibleColumns.includes(key) ||
                          col.value === 'totalPurchasePrice' ||
                          (col.value === 'action' && hasAccess(PERMISSIONS.purchaseRequests.supplierOffers.write)) ||
                          (col.value === 'remove' && hasAccess(PERMISSIONS.purchaseRequests.supplierOffers.delete))
                        );
                      })
                      .map((col, index) => {
                        const total = calculateTotal(dataSource, col.value as keyof SupplierOfferItem);

                        // Custom rendering logic per column key
                        let content: React.ReactNode = '-';

                        if (col.value === 'action' || col.value === 'remove') {
                          content = '';
                        } else if (index === 1) {
                          content = 'Total'; // second cell says "Total"
                        } else if (col.value === 'quantity' && total) {
                          content = total;
                        } else if (col.value === 'purchasePricePerPiece' && total) {
                          content = `${total}€`;
                        } else if (col.value === 'shippingCost' && total) {
                          content = `${total}€`;
                        } else if (col.value === 'fees' && total) {
                          content = `${total}€`;
                        } else if (col.value === 'weight' && total) {
                          content = `${total}kg`;
                        } else if (col.value === 'totalPurchasePrice' && total) {
                          content = `${total}€`;
                        }

                        return (
                          <Table.Summary.Cell
                            key={col.value}
                            index={index}
                            className={
                              col.value === 'totalPurchasePrice'
                                ? 'table-last-total'
                                : col.value === 'action' || col.value === 'remove'
                                  ? 'fixed-columns'
                                  : ''
                            }
                          >
                            {content}
                          </Table.Summary.Cell>
                        );
                      })}
                  </Table.Summary.Row>
                </Table.Summary>
              )
            }
          />
          {!subTable && !isViewOnly && (
            <Row>
              <HasAccess role={PERMISSIONS.leads.lead.write}>
                <Button style={{ color: '#05796B' }} type="link" icon={<PlusOutlined />} onClick={handleAdd}>
                  Add Row
                </Button>
              </HasAccess>
            </Row>
          )}
        </div>
      </div>
      <AddItemModal modalState={addItemModalState} onModalStateChange={onAddItemModalStateChange} />
      <AttachmentDrawer
        drawerState={attachmentDrawerState}
        entityName={subTable ? 'SUPPLIER_OFFER_ALT_ITEM' : 'SUPPLIER_OFFER_ITEM'}
        onExpand={onAttachmentExpand}
        data={drawerAttachments}
      />
      <AddEditModal
        modalState={addAttachmentModalState}
        onModalStateChange={() => setAttachmentItemModalState((oState) => !oState)}
        entityName={subTable ? 'SUPPLIER_OFFER_ALT_ITEM' : 'SUPPLIER_OFFER_ITEM'}
        service={'wrd'}
        entityId={record?.id as string}
      />

      <AddSubstituteModal
        onCancel={() => setAddSubstituteModalState(false)}
        open={addSubstituteModalState}
        originalItemId={record?.id}
      />
      <AddAlternativeModel
        onCancel={() => setAddAlternativeModalState(false)}
        open={addAlternativeModalState}
        originalItemId={record?.id}
      />
      <MarkNotAvailableModel
        onCancel={() => setMarkNotAvailableModalState(false)}
        open={markNotAvailableModalState}
        originalItemId={record?.id}
        record={record}
      />
      <DeleteModal
        isSubTable={subTable}
        onDeleteModalClose={() => setDeleteRecord(false)}
        openModal={deleteRecord}
        recordId={record?.id}
      />
    </>
  );
};
